import { cloneDeep } from "lodash";

import {
  type DbConversionsDateRow,
  type DbForecastV1DateRow,
} from "../../protocols";

import {
  METRIC_POINT_DEFAULTS,
  DEFAULT_FACEBOOK_COLUMN_VALUES,
} from "./defaults";
import {
  type MetricPoint,
  type MetricPointData,
  type MetricSeries,
} from "./types";

export const isMetricPointForFacebookAd = (v: MetricPoint): boolean => {
  return Object.keys(DEFAULT_FACEBOOK_COLUMN_VALUES).some(
    (key) => !!v[key as keyof MetricPoint],
  );
};

/**
 * Parses a {@link MetricPoint} to return an object that consists of only the numeric values,
 * {@link MetricPointData}.
 */
export const parseNumericMetricPointData = (
  point: MetricPoint,
): MetricPointData =>
  Object.keys(point).reduce(
    (prev: MetricPointData, curr: string): MetricPointData => {
      return typeof point[curr as keyof MetricPoint] === "number"
        ? {
            ...prev,
            [curr as keyof MetricPointData]: point[curr as keyof MetricPoint],
          }
        : prev;
    },
    {} as MetricPointData,
  );

export function makeMetricPoint(
  row: DbConversionsDateRow,
  forecastDailyRow: DbForecastV1DateRow | null,
): MetricPoint {
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const { date, adKey, ...rest } = row;

  const rv: MetricPoint = {
    ...rest,
    extraRev_to_30: 0,
    extraRev_to_60: 0,
    extraRev_to_90: 0,

    extraTxns_to_30: 0,
    extraTxns_to_60: 0,
    extraTxns_to_90: 0,
  };

  if (forecastDailyRow) {
    rv.extraRev_to_30 = forecastDailyRow.forecast_rev__30;
    rv.extraRev_to_60 = forecastDailyRow.forecast_rev__60;
    rv.extraRev_to_90 = forecastDailyRow.forecast_rev__90;

    rv.extraTxns_to_30 = forecastDailyRow.forecast_txns__30;
    rv.extraTxns_to_60 = forecastDailyRow.forecast_txns__60;
    rv.extraTxns_to_90 = forecastDailyRow.forecast_txns__90;
  }

  return rv;
}

export const newMetricPoint = (): MetricPoint =>
  cloneDeep(METRIC_POINT_DEFAULTS);

export const addMetricToSeries = <S extends MetricSeries>(
  target: S,
  key: keyof S,
  source: MetricPoint,
): S => ({
  ...target,
  [key]: addMetricToPoint(target[key] || newMetricPoint(), source),
});

/**
 * Adds the provided {@link MetricPoint}(s) to the target (if defined).  If the target,
 * {@link MetricPoint}, is not defined, a new {@link MetricPoint} is used.
 *
 * @param {Array<MetricPoint> | (() => Generator<MetricPoint>)} gen
 *   The {@link MetricPoint}(s) to add to the target.  Can be provided as either an Array of
 *   {@link MetricPoint}(s) or a generator that yields {@link MetricPoint}(s).
 *
 * @param {MetricPoint | undefined} target
 *   The {@link MetricPoint} that the provided metric points should be merged into.  If not
 *   provided, a new {@link MetricPoint} is used with the default state.
 *
 * @returns {@link MetricPoint}
 */
export const addMetricsToPoint = (
  gen: Array<MetricPoint> | (() => Generator<MetricPoint>),
  target?: MetricPoint,
): MetricPoint => {
  let tg = target === undefined ? newMetricPoint() : target;
  if (gen instanceof Array) {
    return gen.reduce(
      (prev: MetricPoint, curr: MetricPoint) => addMetricToPoint(prev, curr),
      tg,
    );
  }
  for (const metric of gen()) {
    tg = addMetricToPoint(tg, metric);
  }
  return tg;
};

/**
 * Adds the provided {@link MetricPoint} data to the target.
 *
 * @param {MetricPoint} target
 *   The {@link MetricPoint} that the provided metric data should be merged into.
 *
 * @param {Partial<MetricPoint>} source
 *   The {@link MetricPoint} data that should be merged into the target.
 *
 * @returns {@link MetricPoint}
 */
export function addMetricToPoint(
  target: MetricPoint,
  source: Partial<MetricPoint>,
): MetricPoint {
  const addKeyValue = <K extends keyof MetricPoint>(
    t: MetricPoint,
    k: K,
    v: MetricPoint[K],
  ) => ({
    ...t,
    [k]: v,
  });

  return {
    ...Object.keys(source).reduce((prev: MetricPoint, key: string) => {
      const k = key as keyof MetricPoint;
      const v = source[k];
      const targetV = target[k];
      if (v !== undefined) {
        if (!targetV) {
          return addKeyValue(prev, k, v);
        } else if (typeof v === "number" && typeof targetV === "number") {
          return addKeyValue(prev, k, targetV + v);
        }
      }
      return prev;
    }, target),
    goals: (source.goals || []).reduce(
      (prev: MetricPoint["goals"], goal: MetricPoint["goals"][number]) => {
        const ind = prev.findIndex(
          (v: MetricPoint["goals"][number]) =>
            v.am === goal.am && v.aw === goal.aw && v.gt === goal.gt,
        );
        if (ind === -1) {
          return [...prev, goal];
        }
        return [
          ...prev.slice(0, ind),
          { ...goal, gc: prev[ind].gc + goal.gc },
          ...prev.slice(ind + 1),
        ];
      },
      target.goals,
    ),
    convs: (source.convs || []).reduce(
      (prev: MetricPoint["convs"], conv: MetricPoint["convs"][number]) => {
        const ind = prev.findIndex(
          (v: MetricPoint["convs"][number]) =>
            v.am === conv.am && v.aw === conv.aw,
        );
        if (ind === -1) {
          return [...prev, conv];
        }
        return [
          ...prev.slice(0, ind),
          {
            ...prev[ind],
            txns: prev[ind].txns + conv.txns,
            rev: prev[ind].rev + conv.rev,
            orderDiscounts: prev[ind].orderDiscounts + conv.orderDiscounts,
            taxValue: prev[ind].taxValue + conv.taxValue,
            shippingValue: prev[ind].shippingValue + conv.shippingValue,
            orderRefundedValue:
              prev[ind].orderRefundedValue + conv.orderRefundedValue,
            ftTxns: prev[ind].ftTxns + conv.ftTxns,
            ftRev: prev[ind].ftRev + conv.ftRev,
            ftOrderDiscounts:
              prev[ind].ftOrderDiscounts + conv.ftOrderDiscounts,
            ftTaxValue: prev[ind].ftTaxValue + conv.ftTaxValue,
            ftShippingValue: prev[ind].ftShippingValue + conv.ftShippingValue,
            ftOrderRefundedValue:
              prev[ind].ftOrderRefundedValue + conv.ftOrderRefundedValue,
            rtnTxns: prev[ind].rtnTxns + conv.rtnTxns,
            rtnRev: prev[ind].rtnRev + conv.rtnRev,
            rtnOrderDiscounts:
              prev[ind].rtnOrderDiscounts + conv.rtnOrderDiscounts,
            rtnTaxValue: prev[ind].rtnTaxValue + conv.rtnTaxValue,
            rtnShippingValue:
              prev[ind].rtnShippingValue + conv.rtnShippingValue,
            rtnOrderRefundedValue:
              prev[ind].rtnOrderRefundedValue + conv.rtnOrderRefundedValue,
            ftOrderDiscountsOrderSubscriptions:
              prev[ind].ftOrderDiscountsOrderSubscriptions +
              conv.ftOrderDiscountsOrderSubscriptions,
            recurringOrderDiscountsOrderSubscriptions:
              prev[ind].recurringOrderDiscountsOrderSubscriptions +
              conv.recurringOrderDiscountsOrderSubscriptions,
            otherOrderDiscountsOrderSubscriptions:
              prev[ind].otherOrderDiscountsOrderSubscriptions +
              conv.otherOrderDiscountsOrderSubscriptions,
            orderDiscountsOrderSubscriptions:
              prev[ind].orderDiscountsOrderSubscriptions +
              conv.orderDiscountsOrderSubscriptions,
            ftTaxValueOrderSubscriptions:
              prev[ind].ftTaxValueOrderSubscriptions +
              conv.ftTaxValueOrderSubscriptions,
            recurringTaxValueOrderSubscriptions:
              prev[ind].recurringTaxValueOrderSubscriptions +
              conv.recurringTaxValueOrderSubscriptions,
            otherTaxValueOrderSubscriptions:
              prev[ind].otherTaxValueOrderSubscriptions +
              conv.otherTaxValueOrderSubscriptions,
            taxValueOrderSubscriptions:
              prev[ind].taxValueOrderSubscriptions +
              conv.taxValueOrderSubscriptions,
            ftShippingValueOrderSubscriptions:
              prev[ind].ftShippingValueOrderSubscriptions +
              conv.ftShippingValueOrderSubscriptions,
            recurringShippingValueOrderSubscriptions:
              prev[ind].recurringShippingValueOrderSubscriptions +
              conv.recurringShippingValueOrderSubscriptions,
            otherShippingValueOrderSubscriptions:
              prev[ind].otherShippingValueOrderSubscriptions +
              conv.otherShippingValueOrderSubscriptions,
            shippingValueOrderSubscriptions:
              prev[ind].shippingValueOrderSubscriptions +
              conv.shippingValueOrderSubscriptions,
            ftOrderRefundedValueOrderSubscriptions:
              prev[ind].ftOrderRefundedValueOrderSubscriptions +
              conv.ftOrderRefundedValueOrderSubscriptions,
            recurringOrderRefundedValueOrderSubscriptions:
              prev[ind].recurringOrderRefundedValueOrderSubscriptions +
              conv.recurringOrderRefundedValueOrderSubscriptions,
            otherOrderRefundedValueOrderSubscriptions:
              prev[ind].otherOrderRefundedValueOrderSubscriptions +
              conv.otherOrderRefundedValueOrderSubscriptions,
            orderRefundedValueOrderSubscriptions:
              prev[ind].orderRefundedValueOrderSubscriptions +
              conv.orderRefundedValueOrderSubscriptions,

            transactionsOrderSubscription:
              prev[ind].transactionsOrderSubscription +
              conv.transactionsOrderSubscription,
            transactionsOrderSubscriptionFirstTime:
              prev[ind].transactionsOrderSubscriptionFirstTime +
              conv.transactionsOrderSubscriptionFirstTime,
            transactionsOrderSubscriptionRecurring:
              prev[ind].transactionsOrderSubscriptionRecurring +
              conv.transactionsOrderSubscriptionRecurring,
            transactionsOrderSubscriptionOther:
              prev[ind].transactionsOrderSubscriptionOther +
              conv.transactionsOrderSubscriptionOther,
            revenueOrderSubscription:
              prev[ind].revenueOrderSubscription +
              conv.revenueOrderSubscription,
            revenueOrderSubscriptionFirstTime:
              prev[ind].revenueOrderSubscriptionFirstTime +
              conv.revenueOrderSubscriptionFirstTime,
            revenueOrderSubscriptionRecurring:
              prev[ind].revenueOrderSubscriptionRecurring +
              conv.revenueOrderSubscriptionRecurring,
            revenueOrderSubscriptionOther:
              prev[ind].revenueOrderSubscriptionOther +
              conv.revenueOrderSubscriptionOther,

            transactionsWebShops:
              prev[ind].transactionsWebShops + conv.transactionsWebShops,
            revenueWebShops: prev[ind].revenueWebShops + conv.revenueWebShops,
            transactionsMetaShops:
              prev[ind].transactionsMetaShops + conv.transactionsMetaShops,
            revenueMetaShops:
              prev[ind].revenueMetaShops + conv.revenueMetaShops,
            transactionsTikTokShops:
              prev[ind].transactionsTikTokShops + conv.transactionsTikTokShops,
            revenueTikTokShops:
              prev[ind].revenueTikTokShops + conv.revenueTikTokShops,
          },
          ...prev.slice(ind + 1),
        ];
      },
      target.convs,
    ),
  };
}

/**
 * @deprecated
 * Deprecated in favor of immutable form, {@link addMetricToSeries}.
 *
 * @see addToMetricPoint for more details.
 */
export function addToMetricSeries(
  target: MetricSeries,
  key: string,
  source: MetricPoint,
) {
  if (!target[key]) {
    target[key] = newMetricPoint();
  }
  addToMetricPoint(target[key], source);
}

/**
 * Disclaimer! This version is hundreds times faster than the immutable version.
 *
 * @remarks
 * - It is safer to avoid mutating the metric properties in place, as mutability can be bug prone,
 *   particularly in React components.
 * - Returning the metric allows typings to both be more specifically defined and more flexible on
 *   the input and return type, and usage, particularly in cases where partial information is being
 *   provided.
 * - Returning the updated metric allows the method to be used in native JS chain operations, such
 *   as map/reduce, and with generators, which has better performance.
 */
export function addToMetricPoint(target: MetricPoint, source: MetricPoint) {
  Object.entries(source).forEach(([key, value]) => {
    if (!target[key as keyof MetricPoint]) {
      target[key as keyof MetricPoint] = value as any;
    } else if (
      typeof value === "number" &&
      typeof target[key as keyof MetricPoint] === "number"
    ) {
      target[key as keyof MetricPoint] += value as any;
    }
  });
  for (const conv of source.convs ?? []) {
    const { am, aw } = conv;
    const targetConv = target.convs.find((v) => v.am === am && v.aw === aw);
    if (targetConv) {
      targetConv.txns += conv.txns;
      targetConv.rev += conv.rev;
      targetConv.orderDiscounts += conv.orderDiscounts;
      targetConv.taxValue += conv.taxValue;
      targetConv.shippingValue += conv.shippingValue;
      targetConv.orderRefundedValue += conv.orderRefundedValue;
      targetConv.ftTxns += conv.ftTxns;
      targetConv.ftRev += conv.ftRev;
      targetConv.ftOrderDiscounts += conv.ftOrderDiscounts;
      targetConv.ftTaxValue += conv.ftTaxValue;
      targetConv.ftShippingValue += conv.ftShippingValue;
      targetConv.ftOrderRefundedValue += conv.ftOrderRefundedValue;
      targetConv.rtnTxns += conv.rtnTxns;
      targetConv.rtnRev += conv.rtnRev;
      targetConv.rtnOrderDiscounts += conv.rtnOrderDiscounts;
      targetConv.rtnTaxValue += conv.rtnTaxValue;
      targetConv.rtnShippingValue += conv.rtnShippingValue;
      targetConv.rtnOrderRefundedValue += conv.rtnOrderRefundedValue;
      targetConv.ftOrderDiscountsOrderSubscriptions +=
        conv.ftOrderDiscountsOrderSubscriptions;
      targetConv.recurringOrderDiscountsOrderSubscriptions +=
        conv.recurringOrderDiscountsOrderSubscriptions;
      targetConv.otherOrderDiscountsOrderSubscriptions +=
        conv.otherOrderDiscountsOrderSubscriptions;
      targetConv.orderDiscountsOrderSubscriptions +=
        conv.orderDiscountsOrderSubscriptions;
      targetConv.ftTaxValueOrderSubscriptions +=
        conv.ftTaxValueOrderSubscriptions;
      targetConv.recurringTaxValueOrderSubscriptions +=
        conv.recurringTaxValueOrderSubscriptions;
      targetConv.otherTaxValueOrderSubscriptions +=
        conv.otherTaxValueOrderSubscriptions;
      targetConv.taxValueOrderSubscriptions += conv.taxValueOrderSubscriptions;
      targetConv.ftShippingValueOrderSubscriptions +=
        conv.ftShippingValueOrderSubscriptions;
      targetConv.recurringShippingValueOrderSubscriptions +=
        conv.recurringShippingValueOrderSubscriptions;
      targetConv.otherShippingValueOrderSubscriptions +=
        conv.otherShippingValueOrderSubscriptions;
      targetConv.shippingValueOrderSubscriptions +=
        conv.shippingValueOrderSubscriptions;
      targetConv.ftOrderRefundedValueOrderSubscriptions +=
        conv.ftOrderRefundedValueOrderSubscriptions;
      targetConv.recurringOrderRefundedValueOrderSubscriptions +=
        conv.recurringOrderRefundedValueOrderSubscriptions;
      targetConv.otherOrderRefundedValueOrderSubscriptions +=
        conv.otherOrderRefundedValueOrderSubscriptions;
      targetConv.orderRefundedValueOrderSubscriptions +=
        conv.orderRefundedValueOrderSubscriptions;

      targetConv.transactionsOrderSubscription +=
        conv.transactionsOrderSubscription;
      targetConv.transactionsOrderSubscriptionFirstTime +=
        conv.transactionsOrderSubscriptionFirstTime;
      targetConv.transactionsOrderSubscriptionRecurring +=
        conv.transactionsOrderSubscriptionRecurring;
      targetConv.transactionsOrderSubscriptionOther +=
        conv.transactionsOrderSubscriptionOther;
      targetConv.revenueOrderSubscription += conv.revenueOrderSubscription;
      targetConv.revenueOrderSubscriptionFirstTime +=
        conv.revenueOrderSubscriptionFirstTime;
      targetConv.revenueOrderSubscriptionRecurring +=
        conv.revenueOrderSubscriptionRecurring;
      targetConv.revenueOrderSubscriptionOther +=
        conv.revenueOrderSubscriptionOther;

      targetConv.transactionsWebShops += conv.transactionsWebShops;
      targetConv.revenueWebShops += conv.revenueWebShops;
      targetConv.transactionsMetaShops += conv.transactionsMetaShops;
      targetConv.revenueMetaShops += conv.revenueMetaShops;
      targetConv.transactionsTikTokShops += conv.transactionsTikTokShops;
      targetConv.revenueTikTokShops += conv.revenueTikTokShops;
    } else {
      target.convs.push({ ...conv });
    }
  }

  for (const goal of source.goals ?? []) {
    const { am, aw, gt } = goal;
    const targetConv = target.goals.find(
      (v) => v.am === am && v.aw === aw && v.gt === gt,
    );
    if (targetConv) {
      targetConv.gc += goal.gc;
    } else {
      target.goals.push({ ...goal });
    }
  }
}

export function _addToMetricPoint(target: MetricPoint, source: MetricPoint) {
  const addKeyValue = <K extends keyof MetricPoint>(
    k: K,
    v: MetricPoint[K],
  ) => {
    target[k] = v;
  };

  Object.entries(source).forEach(([key, v]) => {
    const k = key as keyof MetricPoint;
    const targetV = target[k];
    if (!targetV) {
      addKeyValue(k, v);
    } else if (typeof v === "number" && typeof targetV === "number") {
      addKeyValue(k, targetV + v);
    }
  });

  /* target.spend += source.spend ?? 0;
       target.imprs += source.imprs ?? 0; */

  /* target.pVisits += source.pVisits ?? 0;
       target.pTxns += source.pTxns ?? 0;
       target.pRev += source.pRev ?? 0; */

  /* target.visits += source.visits ?? 0;
       target.newVisits += source.newVisits ?? 0; */

  /* target.extraRev_to_30 += source.extraRev_to_30 ?? 0;
       target.extraRev_to_60 += source.extraRev_to_60 ?? 0;
       target.extraRev_to_90 += source.extraRev_to_90 ?? 0; */

  /* target.extraTxns_to_30 += source.extraTxns_to_30 ?? 0;
       target.extraTxns_to_60 += source.extraTxns_to_60 ?? 0;
       target.extraTxns_to_90 += source.extraTxns_to_90 ?? 0; */

  /* target.transactionsOrderSubscription +=
         source.transactionsOrderSubscription ?? 0;
       target.transactionsOrderSubscriptionFirstTime +=
         source.transactionsOrderSubscriptionFirstTime ?? 0;
       target.transactionsOrderSubscriptionRecurring +=
         source.transactionsOrderSubscriptionRecurring ?? 0;
       target.transactionsOrderSubscriptionOther +=
         source.transactionsOrderSubscriptionOther ?? 0; */

  /* target.revenueOrderSubscription += source.revenueOrderSubscription ?? 0;
       target.revenueOrderSubscriptionFirstTime +=
         source.revenueOrderSubscriptionFirstTime ?? 0;
       target.revenueOrderSubscriptionRecurring +=
         source.revenueOrderSubscriptionRecurring ?? 0;
       target.revenueOrderSubscriptionOther +=
         source.revenueOrderSubscriptionOther ?? 0; */

  /* target.metaTotalPurchases1DClick += source.metaTotalPurchases1DClick ?? 0;
       target.metaTotalPurchases1DView += source.metaTotalPurchases1DView ?? 0;
       target.metaTotalPurchases7DClick += source.metaTotalPurchases7DClick ?? 0;
       target.metaTotalPurchases7DClick1DView +=
         source.metaTotalPurchases7DClick1DView ?? 0;
       target.metaShopPurchases1DClick += source.metaShopPurchases1DClick ?? 0;
       target.metaShopPurchases7DClick += source.metaShopPurchases7DClick ?? 0;
       target.metaShopPurchases7DClick1DView +=
         source.metaShopPurchases7DClick1DView ?? 0;
       target.metaOffsitePixelPurchases1DClick +=
         source.metaOffsitePixelPurchases1DClick ?? 0;
       target.metaOffsitePixelPurchases7DClick +=
         source.metaOffsitePixelPurchases7DClick ?? 0;
       target.metaOffsitePixelPurchases7DClick1DView +=
         source.metaOffsitePixelPurchases7DClick1DView ?? 0;
       target.metaTotalRevenue1DClick += source.metaTotalRevenue1DClick ?? 0;
       target.metaTotalRevenue7DClick += source.metaTotalRevenue7DClick ?? 0;
       target.metaTotalRevenue7DClick1DView +=
         source.metaTotalRevenue7DClick1DView ?? 0;
       target.metaShopRevenue1DClick += source.metaShopRevenue1DClick ?? 0;
       target.metaShopRevenue7DClick += source.metaShopRevenue7DClick ?? 0;
       target.metaShopRevenue7DClick1DView +=
         source.metaShopRevenue7DClick1DView ?? 0;
       target.metaOffsitePixelRevenue1DClick +=
         source.metaOffsitePixelRevenue1DClick ?? 0;
       target.metaOffsitePixelRevenue7DClick +=
         source.metaOffsitePixelRevenue7DClick ?? 0;
       target.metaOffsitePixelRevenue7DClick1DView +=
         source.metaOffsitePixelRevenue7DClick1DView ?? 0;
       target.metaImprs += source.metaImprs ?? 0;
       target.metaVideoPlayActions7DClick1DView +=
         source.metaVideoPlayActions7DClick1DView ?? 0;
       target.metaVideoPlayActions1DView += source.metaVideoPlayActions1DView ?? 0;
       target.metaVideoThruplayWatchedActions7DClick1DView +=
         source.metaVideoThruplayWatchedActions7DClick1DView ?? 0;
       target.metaVideoThruplayWatchedActions1DView +=
         source.metaVideoThruplayWatchedActions1DView ?? 0; */

  /* target.googleAllConversions += source.googleAllConversions ?? 0;
       target.googleAllConversionsValue += source.googleAllConversionsValue ?? 0;
       target.googleImpressions += source.googleImpressions ?? 0;
       target.googleClicks += source.googleClicks ?? 0;
       target.googleVideoViews += source.googleVideoViews ?? 0;
       target.googleViewThroughConversions +=
         source.googleViewThroughConversions ?? 0; */

  /* target.tiktokVideoViews += source.tiktokVideoViews ?? 0;
       target.tiktokVideoViews2s += source.tiktokVideoViews2s ?? 0;
       target.tiktokVideoViews6s += source.tiktokVideoViews6s ?? 0;
       target.tiktokVideoViewsP100 += source.tiktokVideoViewsP100 ?? 0;
       target.tiktokVideoViewsP75 += source.tiktokVideoViewsP75 ?? 0;
       target.tiktokVideoViewsP50 += source.tiktokVideoViewsP50 ?? 0;
       target.tiktokVideoViewsP25 += source.tiktokVideoViewsP25 ?? 0;
       target.tiktokImprs += source.tiktokImprs ?? 0;
       target.tiktokLikes += source.tiktokLikes ?? 0;
       target.tiktokClicks += source.tiktokClicks ?? 0;
       target.tiktokTotalCompletePayment += source.tiktokTotalCompletePayment ?? 0;
       target.tiktokEngagements += source.tiktokEngagements ?? 0; */

  // target.tiktokCompletePayment += source.tiktokCompletePayment ?? 0;

  /* target.meta3SVideoViewsDefault += source.meta3SVideoViewsDefault ?? 0;
       target.metaOfflineRevenue1DClick += source.metaOfflineRevenue1DClick ?? 0;
       target.metaOfflineRevenue7DClick += source.metaOfflineRevenue7DClick ?? 0;
       target.metaOfflineRevenue7DClick1DView +=
         source.metaOfflineRevenue7DClick1DView ?? 0;
       target.metaMobileAppRevenue1DClick += source.metaMobileAppRevenue1DClick ?? 0;
       target.metaMobileAppRevenue7DClick += source.metaMobileAppRevenue7DClick ?? 0;
       target.metaMobileAppRevenue7DClick1DView +=
         source.metaMobileAppRevenue7DClick1DView ?? 0;
       target.metaOfflinePurchases1DClick += source.metaOfflinePurchases1DClick ?? 0;
       target.metaOfflinePurchases7DClick += source.metaOfflinePurchases7DClick ?? 0;
       target.metaOfflinePurchases7DClick1DView +=
         source.metaOfflinePurchases7DClick1DView ?? 0;
       target.metaMobileAppPurchases1DClick +=
         source.metaMobileAppPurchases1DClick ?? 0;
       target.metaMobileAppPurchases7DClick +=
         source.metaMobileAppPurchases7DClick ?? 0;
       target.metaMobileAppPurchases7DClick1DView +=
         source.metaMobileAppPurchases7DClick1DView ?? 0; */

  /* target.metaShopPurchases1DView += source.metaShopPurchases1DView;
       target.metaOfflinePurchases1DView += source.metaOfflinePurchases1DView;
       target.metaMobileAppPurchases1DView += source.metaMobileAppPurchases1DView;
       target.metaOffsitePixelPurchases1DView +=
         source.metaOffsitePixelPurchases1DView;
       target.metaTotalRevenue1DView += source.metaTotalRevenue1DView;
       target.metaShopRevenue1DView += source.metaShopRevenue1DView;
       target.metaOfflineRevenue1DView += source.metaOfflineRevenue1DView;
       target.metaOffsitePixelRevenue1DView += source.metaOffsitePixelRevenue1DView;
       target.metaMobileAppRevenue1DView += source.metaMobileAppRevenue1DView;
       target.metaOutboundClicks += source.metaOutboundClicks;
       target.metaLinkClicks += source.metaLinkClicks; */

  /* target.snapchatSpend += source.snapchatSpend;
       target.snapchatPaidImpressions += source.snapchatPaidImpressions;
       target.snapchatEarnedImpressions += source.snapchatEarnedImpressions;
       target.snapchatTotalImpressions += source.snapchatTotalImpressions;
       target.snapchatSwipes += source.snapchatSwipes;
       target.snapchatProfileClicks += source.snapchatProfileClicks;
       target.snapchatVideoViews += source.snapchatVideoViews;
       target.snapchatVideoViewsTimeBased += source.snapchatVideoViewsTimeBased;
       target.snapchatVideoViews15S += source.snapchatVideoViews15S;
       target.snapchatQuartile1 += source.snapchatQuartile1;
       target.snapchatQuartile2 += source.snapchatQuartile2;
       target.snapchatQuartile3 += source.snapchatQuartile3;
       target.snapchatViewCompletion += source.snapchatViewCompletion;
       target.snapchatAttachmentTotalViewTimeMillis +=
         source.snapchatAttachmentTotalViewTimeMillis;
       target.snapchatRevenueMicrocurrency += source.snapchatRevenueMicrocurrency;
       target.snapchatTransactions += source.snapchatTransactions; */

  /* target.meta3SVideoViews1DClick += source.meta3SVideoViews1DClick;
       target.meta3SVideoViews7DClick += source.meta3SVideoViews7DClick;
       target.meta3SVideoViews7DClick1DView += source.meta3SVideoViews7DClick1DView;
       target.metaVideoP25Watched += source.metaVideoP25Watched;
       target.metaVideoP50Watched += source.metaVideoP50Watched;
       target.metaVideoP75Watched += source.metaVideoP75Watched;
       target.metaVideoP95Watched += source.metaVideoP95Watched;
       target.metaVideoP100Watched += source.metaVideoP100Watched; */

  /* target.pinterestCheckoutConversions += source.pinterestCheckoutConversions;
       target.pinterestCheckoutOrderValue += source.pinterestCheckoutOrderValue;
       target.pinterestPageVisitConversions += source.pinterestPageVisitConversions;
       target.pinterestAddToCartClickConversions +=
         source.pinterestAddToCartClickConversions;
       target.pinterestPaidImpressions += source.pinterestPaidImpressions;
       target.pinterestPaidEngagements += source.pinterestPaidEngagements;
       target.pinterestPaidPinClicks += source.pinterestPaidPinClicks;
       target.pinterestPaidSaves += source.pinterestPaidSaves;
       target.pinterestPaidIdeaPinProductTagVisit +=
         source.pinterestPaidIdeaPinProductTagVisit;
       target.pinterestSpendInAccountCurrency +=
         source.pinterestSpendInAccountCurrency;
       target.pinterestPaidVideoViews += source.pinterestPaidVideoViews;
       target.pinterestPaid3SecVideoViews += source.pinterestPaid3SecVideoViews;
       target.pinterestPaidVideoPlayedAt25 += source.pinterestPaidVideoPlayedAt25;
       target.pinterestPaidVideoPlayedAt50 += source.pinterestPaidVideoPlayedAt50;
       target.pinterestPaidVideoPlayedAt75 += source.pinterestPaidVideoPlayedAt75;
       target.pinterestPaidVideoPlayedAt95 += source.pinterestPaidVideoPlayedAt95;
       target.pinterestPaidVideoPlayedAt100 += source.pinterestPaidVideoPlayedAt100;
       target.pinterestPaidVideoStarts += source.pinterestPaidVideoStarts; */

  for (const conv of source.convs ?? []) {
    const { am, aw } = conv;
    const targetConv = target.convs.find((v) => v.am === am && v.aw === aw);
    if (targetConv) {
      targetConv.txns += conv.txns;
      targetConv.rev += conv.rev;
      targetConv.orderDiscounts += conv.orderDiscounts;
      targetConv.taxValue += conv.taxValue;
      targetConv.shippingValue += conv.shippingValue;
      targetConv.orderRefundedValue += conv.orderRefundedValue;
      targetConv.ftTxns += conv.ftTxns;
      targetConv.ftRev += conv.ftRev;
      targetConv.ftOrderDiscounts += conv.ftOrderDiscounts;
      targetConv.ftTaxValue += conv.ftTaxValue;
      targetConv.ftShippingValue += conv.ftShippingValue;
      targetConv.ftOrderRefundedValue += conv.ftOrderRefundedValue;
      targetConv.rtnTxns += conv.rtnTxns;
      targetConv.rtnRev += conv.rtnRev;
      targetConv.rtnOrderDiscounts += conv.rtnOrderDiscounts;
      targetConv.rtnTaxValue += conv.rtnTaxValue;
      targetConv.rtnShippingValue += conv.rtnShippingValue;
      targetConv.rtnOrderRefundedValue += conv.rtnOrderRefundedValue;
      targetConv.ftOrderDiscountsOrderSubscriptions +=
        conv.ftOrderDiscountsOrderSubscriptions;
      targetConv.recurringOrderDiscountsOrderSubscriptions +=
        conv.recurringOrderDiscountsOrderSubscriptions;
      targetConv.otherOrderDiscountsOrderSubscriptions +=
        conv.otherOrderDiscountsOrderSubscriptions;
      targetConv.orderDiscountsOrderSubscriptions +=
        conv.orderDiscountsOrderSubscriptions;
      targetConv.ftTaxValueOrderSubscriptions +=
        conv.ftTaxValueOrderSubscriptions;
      targetConv.recurringTaxValueOrderSubscriptions +=
        conv.recurringTaxValueOrderSubscriptions;
      targetConv.otherTaxValueOrderSubscriptions +=
        conv.otherTaxValueOrderSubscriptions;
      targetConv.taxValueOrderSubscriptions += conv.taxValueOrderSubscriptions;
      targetConv.ftShippingValueOrderSubscriptions +=
        conv.ftShippingValueOrderSubscriptions;
      targetConv.recurringShippingValueOrderSubscriptions +=
        conv.recurringShippingValueOrderSubscriptions;
      targetConv.otherShippingValueOrderSubscriptions +=
        conv.otherShippingValueOrderSubscriptions;
      targetConv.shippingValueOrderSubscriptions +=
        conv.shippingValueOrderSubscriptions;
      targetConv.ftOrderRefundedValueOrderSubscriptions +=
        conv.ftOrderRefundedValueOrderSubscriptions;
      targetConv.recurringOrderRefundedValueOrderSubscriptions +=
        conv.recurringOrderRefundedValueOrderSubscriptions;
      targetConv.otherOrderRefundedValueOrderSubscriptions +=
        conv.otherOrderRefundedValueOrderSubscriptions;
      targetConv.orderRefundedValueOrderSubscriptions +=
        conv.orderRefundedValueOrderSubscriptions;

      targetConv.transactionsOrderSubscription +=
        conv.transactionsOrderSubscription;
      targetConv.transactionsOrderSubscriptionFirstTime +=
        conv.transactionsOrderSubscriptionFirstTime;
      targetConv.transactionsOrderSubscriptionRecurring +=
        conv.transactionsOrderSubscriptionRecurring;
      targetConv.transactionsOrderSubscriptionOther +=
        conv.transactionsOrderSubscriptionOther;
      targetConv.revenueOrderSubscription += conv.revenueOrderSubscription;
      targetConv.revenueOrderSubscriptionFirstTime +=
        conv.revenueOrderSubscriptionFirstTime;
      targetConv.revenueOrderSubscriptionRecurring +=
        conv.revenueOrderSubscriptionRecurring;
      targetConv.revenueOrderSubscriptionOther +=
        conv.revenueOrderSubscriptionOther;

      targetConv.transactionsWebShops += conv.transactionsWebShops;
      targetConv.revenueWebShops += conv.revenueWebShops;
      targetConv.transactionsMetaShops += conv.transactionsMetaShops;
      targetConv.revenueMetaShops += conv.revenueMetaShops;
      targetConv.transactionsTikTokShops += conv.transactionsTikTokShops;
      targetConv.revenueTikTokShops += conv.revenueTikTokShops;
    } else {
      target.convs.push({ ...conv });
    }
  }

  for (const goal of source.goals ?? []) {
    const { am, aw, gt } = goal;
    const targetConv = target.goals.find(
      (v) => v.am === am && v.aw === aw && v.gt === gt,
    );
    if (targetConv) {
      targetConv.gc += goal.gc;
    } else {
      target.goals.push({ ...goal });
    }
  }
}
